import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import {
  Phone,
  MapPin,
  Building2,
  Users,
  Send,
  Loader2,
  Clock,
  Image,
  FileText,
  Play,
  Pause,
  SkipForward,
  Check,
  AlertTriangle,
  Settings,
  Calendar,
  Upload,
  Bold,
  Italic,
  Link,
  Smile,
  X,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  ClipboardList,
} from "lucide-react";

const API = window.location.origin + "/api";

const REGIONI_ITALIANE = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
];

const EMOJI_LIST = ["👋", "✨", "💆‍♀️", "💅", "🌸", "💜", "🎁", "📞", "📍", "⭐", "💝", "🌟", "💫", "🔥", "💪"];

const WhatsAppAutomationPage = () => {
  // States
  const [contatti, setContatti] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRegione, setSelectedRegione] = useState("");
  const [selectedContatti, setSelectedContatti] = useState([]);
  const [step, setStep] = useState(1); // 1: Select, 2: Compose, 3: Schedule, 4: Send
  
  // Test mode
  const [testMode, setTestMode] = useState(false);
  const [testNumero, setTestNumero] = useState("");
  const [testNome, setTestNome] = useState("Test Centro");
  const [testMessaggio, setTestMessaggio] = useState("");
  
  // Message composition
  const [messaggio, setMessaggio] = useState("");
  const [allegati, setAllegati] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Scheduling
  const [invioImmediato, setInvioImmediato] = useState(true);
  const [dataInvio, setDataInvio] = useState("");
  const [oraInvio, setOraInvio] = useState("09:00");
  const [intervalloSecondi, setIntervalloSecondi] = useState(45);
  const [messaggiPerSessione, setMessaggiPerSessione] = useState(30);
  const [pausaMinuti, setPausaMinuti] = useState(30);
  
  // Sending state
  const [sendingActive, setSendingActive] = useState(false);
  const [sendingPaused, setSendingPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [sentCount, setSentCount] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);
  
  // Template
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [templates, setTemplates] = useState([
    {
      nome: "Presentazione ODR",
      messaggio: "👋 Buongiorno {nome}!\n\n✨ Siamo ODR Academy, l'accademia di formazione in Estetica Oncologica.\n\n💆‍♀️ Offriamo corsi certificati per professionisti del settore beauty.\n\n📞 Per info: accademia.odr@ita318.it\n\n🌸 Team ODR Academy"
    },
    {
      nome: "Invito Evento",
      messaggio: "🌟 Ciao {nome}!\n\nTi invitiamo al nostro prossimo evento formativo.\n\n📅 Data: [DATA]\n📍 Luogo: [LUOGO]\n\n✨ Posti limitati!\n\nRispondi per maggiori info."
    }
  ]);

  // Fetch contacts
  const fetchContatti = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedRegione) params.append("regione", selectedRegione);
      params.append("limit", "5000");
      
      const res = await axios.get(`${API}/admin/contatti-marketing?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContatti(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento contatti");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/contatti-marketing/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchContatti();
    fetchStats();
  }, [selectedRegione]);

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa: JPEG, PNG, GIF, PDF");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande. Max 5MB");
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      // Create full URL for the attachment
      // Get the base URL (without /api)
      const baseUrl = API.replace('/api', '');
      let fullUrl = res.data.url;
      
      // If the URL is relative, make it absolute
      if (fullUrl.startsWith('/')) {
        fullUrl = baseUrl + fullUrl;
      }

      setAllegati([...allegati, {
        nome: file.name,
        url: fullUrl,
        tipo: file.type
      }]);
      toast.success("File caricato!");
    } catch (error) {
      toast.error("Errore nel caricamento file");
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAllegato = (index) => {
    setAllegati(allegati.filter((_, i) => i !== index));
  };

  // Select all contacts
  const handleSelectAll = () => {
    if (selectedContatti.length === contatti.length) {
      setSelectedContatti([]);
    } else {
      setSelectedContatti(contatti.map(c => c.contatto_id));
    }
  };

  // Toggle single contact
  const toggleContact = (id) => {
    if (selectedContatti.includes(id)) {
      setSelectedContatti(selectedContatti.filter(c => c !== id));
    } else {
      setSelectedContatti([...selectedContatti, id]);
    }
  };

  // Select by region - fetch contacts for that region if not loaded
  const handleSelectRegione = async (regione) => {
    try {
      const token = localStorage.getItem("token");
      
      // Fetch all contacts for this region
      const res = await axios.get(`${API}/admin/contatti-marketing?regione=${encodeURIComponent(regione)}&limit=10000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const regioneContatti = res.data.filter(c => c.cellulare); // Only those with phone
      const regioneIds = regioneContatti.map(c => c.contatto_id);
      
      if (regioneIds.length === 0) {
        toast.error(`Nessun contatto con telefono nella regione ${regione}`);
        return;
      }
      
      // Check if all from this region are already selected
      const allSelected = regioneIds.every(id => selectedContatti.includes(id));
      
      if (allSelected) {
        // Deselect all from this region
        setSelectedContatti(selectedContatti.filter(id => !regioneIds.includes(id)));
        // Remove these contacts from the list
        setContatti(prev => prev.filter(c => c.regione !== regione));
        toast.info(`${regione} deselezionata`);
      } else {
        // Add region contacts to the list (avoiding duplicates)
        setContatti(prev => {
          const existingIds = new Set(prev.map(c => c.contatto_id));
          const newContacts = regioneContatti.filter(c => !existingIds.has(c.contatto_id));
          return [...prev, ...newContacts];
        });
        // Select all from this region
        setSelectedContatti(prev => [...new Set([...prev, ...regioneIds])]);
        toast.success(`${regioneIds.length} contatti selezionati da ${regione}`);
      }
    } catch (error) {
      toast.error(`Errore nel caricamento contatti di ${regione}`);
      console.error(error);
    }
  };

  // Generate WhatsApp links
  const generateWhatsAppLinks = () => {
    const selectedContactsData = contatti.filter(c => selectedContatti.includes(c.contatto_id));
    
    const links = selectedContactsData.map((contatto, idx) => {
      if (!contatto.cellulare) return null;
      
      // Clean phone number
      let phone = contatto.cellulare.replace(/[\s\-\.]/g, '');
      if (phone.startsWith("00")) phone = phone.slice(2);
      else if (phone.startsWith("0")) phone = "39" + phone.slice(1);
      else if (!phone.startsWith("39") && !phone.startsWith("+39")) phone = "39" + phone;
      phone = phone.replace("+", "");
      
      // Personalize message
      let msg = messaggio
        .replace(/{nome}/g, contatto.nome_centro || "")
        .replace(/{citta}/g, contatto.citta || "")
        .replace(/{regione}/g, contatto.regione || "");
      
      // Add attachment links if any - formatted nicely
      if (allegati.length > 0) {
        msg += "\n\n━━━━━━━━━━━━━━━";
        msg += "\n📎 *ALLEGATI:*\n";
        allegati.forEach((a) => {
          const emoji = a.tipo.startsWith('image') ? '🖼️' : '📄';
          msg += `\n${emoji} *${a.nome}*`;
          msg += `\n👉 ${a.url}\n`;
        });
        msg += "━━━━━━━━━━━━━━━";
      }
      
      const encodedMsg = encodeURIComponent(msg);
      
      return {
        idx: idx + 1,
        nome: contatto.nome_centro,
        citta: contatto.citta,
        regione: contatto.regione,
        cellulare: contatto.cellulare,
        link: `https://wa.me/${phone}?text=${encodedMsg}`,
        sent: false
      };
    }).filter(Boolean);
    
    setWhatsappLinks(links);
    return links;
  };

  // Send test WhatsApp message
  const sendTestMessage = () => {
    if (!testNumero) {
      toast.error("Inserisci il numero di telefono del destinatario");
      return;
    }
    if (!testMessaggio) {
      toast.error("Inserisci il messaggio da inviare");
      return;
    }
    
    // Clean phone number
    let phone = testNumero.replace(/[\s\-\.]/g, '');
    if (phone.startsWith("00")) phone = phone.slice(2);
    else if (phone.startsWith("0")) phone = "39" + phone.slice(1);
    else if (!phone.startsWith("39") && !phone.startsWith("+39")) phone = "39" + phone;
    phone = phone.replace("+", "");
    
    // Personalize message
    let msg = testMessaggio
      .replace(/{nome}/g, testNome || "")
      .replace(/{citta}/g, "Milano")
      .replace(/{regione}/g, "Lombardia");
    
    // Add attachment links if any - formatted nicely
    if (allegati.length > 0) {
      msg += "\n\n━━━━━━━━━━━━━━━";
      msg += "\n📎 *ALLEGATI:*\n";
      allegati.forEach((a, i) => {
        const emoji = a.tipo.startsWith('image') ? '🖼️' : '📄';
        msg += `\n${emoji} *${a.nome}*`;
        msg += `\n👉 ${a.url}\n`;
      });
      msg += "━━━━━━━━━━━━━━━";
    }
    
    const encodedMsg = encodeURIComponent(msg);
    const waLink = `https://wa.me/${phone}?text=${encodedMsg}`;
    
    // Open WhatsApp
    window.open(waLink, '_blank');
    toast.success("Link WhatsApp aperto! Invia il messaggio dalla finestra di WhatsApp");
  };

  // Start sending process
  const startSending = () => {
    const links = generateWhatsAppLinks();
    if (links.length === 0) {
      toast.error("Nessun contatto con numero di telefono");
      return;
    }
    
    setSendingActive(true);
    setSendingPaused(false);
    setCurrentIndex(0);
    setSentCount(0);
    setStep(4);
    
    // Open first link
    window.open(links[0].link, '_blank');
    setSentCount(1);
    setCurrentIndex(1);
    
    // Start countdown for next
    if (links.length > 1) {
      startCountdown();
    }
  };

  // Countdown timer
  const startCountdown = () => {
    setCountdown(intervalloSecondi);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Open next WhatsApp link
  const openNextLink = () => {
    if (currentIndex >= whatsappLinks.length) {
      toast.success("Invio completato!");
      setSendingActive(false);
      return;
    }
    
    // Check if we need a longer pause
    if (sentCount > 0 && sentCount % messaggiPerSessione === 0) {
      toast.info(`Pausa di ${pausaMinuti} minuti per evitare blocchi`);
      setCountdown(pausaMinuti * 60);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return;
    }
    
    window.open(whatsappLinks[currentIndex].link, '_blank');
    setSentCount(prev => prev + 1);
    setCurrentIndex(prev => prev + 1);
    
    // Update sent status
    const updatedLinks = [...whatsappLinks];
    updatedLinks[currentIndex].sent = true;
    setWhatsappLinks(updatedLinks);
    
    // Start countdown for next
    if (currentIndex + 1 < whatsappLinks.length) {
      startCountdown();
    } else {
      toast.success("Tutti i messaggi sono stati inviati!");
      setSendingActive(false);
    }
  };

  // Pause sending
  const pauseSending = () => {
    setSendingPaused(true);
    clearInterval(countdownRef.current);
  };

  // Resume sending
  const resumeSending = () => {
    setSendingPaused(false);
    startCountdown();
  };

  // Skip to next
  const skipToNext = () => {
    clearInterval(countdownRef.current);
    openNextLink();
  };

  // Open multiple links
  const openMultipleLinks = (count) => {
    const endIndex = Math.min(currentIndex + count, whatsappLinks.length);
    for (let i = currentIndex; i < endIndex; i++) {
      setTimeout(() => {
        window.open(whatsappLinks[i].link, '_blank');
      }, (i - currentIndex) * 2000);
    }
    setSentCount(prev => prev + (endIndex - currentIndex));
    setCurrentIndex(endIndex);
  };

  // Insert emoji
  const insertEmoji = (emoji) => {
    setMessaggio(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Load template
  const loadTemplate = (template) => {
    setMessaggio(template.messaggio);
    toast.success(`Template "${template.nome}" caricato`);
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get contacts with phone
  const contattiConTelefono = contatti.filter(c => c.cellulare);

  return (
    <PageLayout title="Automazione WhatsApp" subtitle="Invio massivo controllato">
      {/* Progress Steps */}
      <div className="mb-6">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          {[
            { num: 1, label: "Seleziona Destinatari" },
            { num: 2, label: "Componi Messaggio" },
            { num: 3, label: "Impostazioni Invio" },
            { num: 4, label: "Invio" }
          ].map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                  step >= s.num 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 text-sm hidden sm:block ${step >= s.num ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                {s.label}
              </span>
              {i < 3 && <ChevronRight className="w-5 h-5 mx-2 text-gray-300" />}
            </div>
          ))}
        </div>
      </div>

      {/* Test Rapido Card - Simulazione completa */}
      <Card className="mb-6 border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Phone className="w-5 h-5 text-orange-600" />
            🧪 Test Rapido WhatsApp - Simulazione Completa
            <Badge className="bg-orange-100 text-orange-700 ml-2">Prova invio reale</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Riga 1: Numero e Nome */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-base font-bold text-orange-700 flex items-center gap-2">
                📱 NUMERO DESTINATARIO *
              </Label>
              <Input
                value={testNumero}
                onChange={(e) => setTestNumero(e.target.value)}
                placeholder="Es: 3471234567"
                className="rounded-xl mt-2 text-lg h-12 border-2 border-orange-300 font-mono"
              />
            </div>
            <div>
              <Label className="text-base font-bold text-gray-700">Nome destinatario</Label>
              <Input
                value={testNome}
                onChange={(e) => setTestNome(e.target.value)}
                placeholder="Es: Centro Estetico Bella"
                className="rounded-xl mt-2 h-12"
              />
              <p className="text-xs text-gray-500 mt-1">Verrà usato per sostituire {"{nome}"} nel messaggio</p>
            </div>
          </div>

          {/* Template rapidi */}
          <div>
            <Label className="text-sm font-medium mb-2 block">📝 Template rapidi</Label>
            <div className="flex flex-wrap gap-2">
              {templates.map((t, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => setTestMessaggio(t.messaggio)}
                  className="rounded-full text-xs"
                >
                  {t.nome}
                </Button>
              ))}
            </div>
          </div>

          {/* Editor messaggio con formattazione */}
          <div>
            <Label className="text-base font-bold text-gray-700">✍️ Messaggio</Label>
            
            {/* Toolbar formattazione */}
            <div className="flex items-center gap-2 p-2 bg-white rounded-t-xl border border-b-0 border-gray-200 mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTestMessaggio(prev => prev + "*testo grassetto*")}
                title="Grassetto"
                className="h-8 w-8 p-0"
              >
                <Bold className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTestMessaggio(prev => prev + "_testo corsivo_")}
                title="Corsivo"
                className="h-8 w-8 p-0"
              >
                <Italic className="w-4 h-4" />
              </Button>
              <div className="h-4 w-px bg-gray-300" />
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="h-8 w-8 p-0"
                >
                  <Smile className="w-4 h-4" />
                </Button>
                {showEmojiPicker && (
                  <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border z-50 flex flex-wrap gap-1 w-56">
                    {EMOJI_LIST.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => {
                          setTestMessaggio(prev => prev + emoji);
                          setShowEmojiPicker(false);
                        }}
                        className="w-8 h-8 hover:bg-gray-100 rounded text-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => setTestMessaggio(prev => prev + "{nome}")}>
                + {"{nome}"}
              </Badge>
              <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => setTestMessaggio(prev => prev + "{citta}")}>
                + {"{citta}"}
              </Badge>
            </div>
            
            <Textarea
              value={testMessaggio}
              onChange={(e) => setTestMessaggio(e.target.value)}
              placeholder="Scrivi il messaggio di test...&#10;&#10;Esempio:&#10;👋 Ciao {nome}!&#10;✨ Ti contattiamo da ODR Academy..."
              className="rounded-t-none rounded-b-xl min-h-[150px] font-mono text-sm"
            />
          </div>

          {/* Upload file e URL esterni */}
          <div>
            <Label className="text-base font-bold text-gray-700 mb-2 block">📎 Allegati (opzionale)</Label>
            
            {/* Upload file locale */}
            <div className="flex items-center gap-3 mb-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/jpeg,image/png,image/gif,application/pdf"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="rounded-full"
              >
                {uploadingFile ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Carica File
              </Button>
              <span className="text-xs text-gray-500">JPEG, PNG, GIF, PDF (max 5MB)</span>
            </div>

            {/* URL esterno (postimg, imgur, ecc.) */}
            <div className="flex items-center gap-2 mb-3">
              <Input
                id="urlEsterno"
                placeholder="Incolla URL immagine (postimg, imgur, ecc.)"
                className="rounded-xl flex-1"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const url = e.target.value.trim();
                    if (url) {
                      // Determine file type from URL
                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || url.includes('postimg') || url.includes('imgur');
                      const fileName = url.split('/').pop() || 'Immagine';
                      setAllegati([...allegati, {
                        nome: fileName,
                        url: url,
                        tipo: isImage ? 'image/external' : 'application/external'
                      }]);
                      e.target.value = '';
                      toast.success("URL aggiunto!");
                    }
                  }
                }}
              />
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => {
                  const input = document.getElementById('urlEsterno');
                  const url = input.value.trim();
                  if (url) {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || url.includes('postimg') || url.includes('imgur');
                    const fileName = url.split('/').pop() || 'Immagine';
                    setAllegati([...allegati, {
                      nome: fileName,
                      url: url,
                      tipo: isImage ? 'image/external' : 'application/external'
                    }]);
                    input.value = '';
                    toast.success("URL aggiunto!");
                  } else {
                    toast.error("Inserisci un URL valido");
                  }
                }}
              >
                <Link className="w-4 h-4 mr-2" />
                Aggiungi URL
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              💡 Puoi incollare link da: postimg.cc, imgur.com, o qualsiasi URL di immagine diretta
            </p>

            {allegati.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {allegati.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-xl border border-green-200">
                    {a.tipo.startsWith('image') ? (
                      <Image className="w-4 h-4 text-green-600" />
                    ) : (
                      <FileText className="w-4 h-4 text-green-600" />
                    )}
                    <span className="text-sm text-green-700 max-w-[200px] truncate">{a.nome}</span>
                    <button onClick={() => removeAllegato(i)} className="text-red-500 hover:text-red-700 ml-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Anteprima WhatsApp realistica */}
          <div>
            <Label className="text-base font-bold text-gray-700 mb-2 block">👁️ Anteprima Messaggio</Label>
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#e5ddd5', backgroundImage: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAOxAAADsQBlSsOGwAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAABNSURBVGiB7c8BDQAwDAOw+je9OzqghwgJBQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABg1x+bB+sB2bsAAAAASUVORK5CYII=")' }}>
              <div className="max-w-[85%] ml-auto">
                {/* Bolla messaggio */}
                <div className="bg-[#dcf8c6] p-3 rounded-lg shadow-sm relative">
                  {/* Freccia bolla */}
                  <div className="absolute -right-2 top-0 w-0 h-0 border-l-8 border-l-[#dcf8c6] border-t-8 border-t-transparent border-b-8 border-b-transparent"></div>
                  
                  {/* Allegati preview */}
                  {allegati.length > 0 && (
                    <div className="mb-2 pb-2 border-b border-green-300">
                      {allegati.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          {a.tipo.startsWith('image') ? (
                            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                              <Image className="w-6 h-6 text-gray-400" />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 p-2 bg-white rounded">
                              <FileText className="w-5 h-5 text-red-500" />
                              <span className="text-xs">{a.nome}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Testo messaggio */}
                  <p className="text-sm whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word' }}>
                    {(testMessaggio || "Il tuo messaggio apparirà qui...")
                      .replace(/{nome}/g, testNome || "[Nome]")
                      .replace(/{citta}/g, "Milano")
                      .replace(/{regione}/g, "Lombardia")
                      .replace(/\*([^*]+)\*/g, '<b>$1</b>')
                      .replace(/_([^_]+)_/g, '<i>$1</i>')
                      .split('\n').map((line, i) => <span key={i}>{line}<br/></span>)
                    }
                  </p>
                  
                  {/* Link allegati */}
                  {allegati.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-green-300 text-xs text-blue-600">
                      📎 {allegati.length} allegato/i inclusi
                    </div>
                  )}
                  
                  {/* Ora invio */}
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-[10px] text-gray-500">
                      {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="text-blue-500 text-xs">✓✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pulsante invio */}
          <Button
            onClick={sendTestMessage}
            disabled={!testNumero || !testMessaggio}
            className="w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 h-14 text-lg font-bold shadow-lg"
          >
            <Send className="w-6 h-6 mr-3" />
            INVIA TEST SU WHATSAPP
          </Button>
          
          <p className="text-center text-sm text-gray-600">
            Cliccando si aprirà WhatsApp con il messaggio pronto. Dovrai solo premere "Invia".
          </p>
        </CardContent>
      </Card>

      {/* Step 1: Select Recipients */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Phone className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">{contattiConTelefono.length}</p>
                    <p className="text-xs text-green-600">Con telefono</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold text-purple-700">{selectedContatti.length}</p>
                    <p className="text-xs text-purple-600">Selezionati</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{Object.keys(stats.per_regione || {}).length}</p>
                    <p className="text-xs text-blue-600">Regioni</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-amber-600" />
                  <div>
                    <p className="text-2xl font-bold text-amber-700">
                      ~{Math.ceil(selectedContatti.length * intervalloSecondi / 60)} min
                    </p>
                    <p className="text-xs text-amber-600">Tempo stimato</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Region Filter & Quick Select */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-600" />
                Seleziona per Regione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.per_regione || {}).map(([regione, count]) => {
                  const regioneContatti = contatti.filter(c => c.regione === regione);
                  const regioneIds = regioneContatti.map(c => c.contatto_id);
                  const selectedInRegione = regioneIds.filter(id => selectedContatti.includes(id)).length;
                  const isFullySelected = selectedInRegione === regioneIds.length && regioneIds.length > 0;
                  
                  return (
                    <Button
                      key={regione}
                      variant={isFullySelected ? "default" : "outline"}
                      size="sm"
                      className={`rounded-full ${isFullySelected ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      onClick={() => handleSelectRegione(regione)}
                    >
                      {regione} ({count})
                      {selectedInRegione > 0 && !isFullySelected && (
                        <Badge variant="secondary" className="ml-1 text-xs">{selectedInRegione}</Badge>
                      )}
                    </Button>
                  );
                })}
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="rounded-full"
                >
                  {selectedContatti.length === contatti.length ? "Deseleziona tutti" : "Seleziona tutti"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedContatti([])}
                  className="rounded-full"
                >
                  Pulisci selezione
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Contacts List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Elenco Contatti ({contattiConTelefono.length} con telefono)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[400px] overflow-y-auto space-y-1">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                  </div>
                ) : contattiConTelefono.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nessun contatto con numero di telefono</p>
                ) : (
                  contattiConTelefono.map(contatto => (
                    <div
                      key={contatto.contatto_id}
                      onClick={() => toggleContact(contatto.contatto_id)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedContatti.includes(contatto.contatto_id)
                          ? 'bg-green-100 border-2 border-green-400'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <Checkbox checked={selectedContatti.includes(contatto.contatto_id)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{contatto.nome_centro}</p>
                        <p className="text-sm text-gray-500">{contatto.citta} - {contatto.regione}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono text-green-600">{contatto.cellulare}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Next Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => setStep(2)}
              disabled={selectedContatti.length === 0}
              className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 px-8"
            >
              Avanti <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Compose Message */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Templates */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Template Rapidi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {templates.map((t, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    size="sm"
                    onClick={() => loadTemplate(t)}
                    className="rounded-full"
                  >
                    {t.nome}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Message Composer */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Componi Messaggio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Formatting toolbar */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMessaggio(prev => prev + "*testo*")}
                  title="Grassetto"
                >
                  <Bold className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMessaggio(prev => prev + "_testo_")}
                  title="Corsivo"
                >
                  <Italic className="w-4 h-4" />
                </Button>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile className="w-4 h-4" />
                  </Button>
                  {showEmojiPicker && (
                    <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border z-10 flex flex-wrap gap-1 w-48">
                      {EMOJI_LIST.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => insertEmoji(emoji)}
                          className="w-8 h-8 hover:bg-gray-100 rounded text-lg"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="h-4 w-px bg-gray-300" />
                <Badge variant="outline" className="text-xs">
                  {`{nome}`} = Nome centro
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {`{citta}`} = Città
                </Badge>
              </div>

              {/* Textarea */}
              <Textarea
                value={messaggio}
                onChange={(e) => setMessaggio(e.target.value)}
                placeholder="Scrivi il tuo messaggio WhatsApp...&#10;&#10;Usa {nome} per il nome del centro&#10;Usa {citta} per la città&#10;&#10;Formattazione WhatsApp:&#10;*grassetto* _corsivo_ ~barrato~"
                className="min-h-[200px] rounded-xl font-mono text-sm"
              />

              {/* Character count */}
              <div className="flex justify-between text-sm text-gray-500">
                <span>Caratteri: {messaggio.length}</span>
                <span className={messaggio.length > 1000 ? 'text-red-500' : ''}>
                  {messaggio.length > 1000 && "Messaggio troppo lungo!"}
                </span>
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label>Allegati (link inclusi nel messaggio)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/jpeg,image/png,image/gif,application/pdf"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="rounded-full"
                  >
                    {uploadingFile ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Carica File
                  </Button>
                  <span className="text-xs text-gray-500">JPEG, PNG, GIF, PDF (max 5MB)</span>
                </div>

                {allegati.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {allegati.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full border border-green-200">
                        {a.tipo.startsWith('image') ? (
                          <Image className="w-4 h-4 text-green-600" />
                        ) : (
                          <FileText className="w-4 h-4 text-green-600" />
                        )}
                        <span className="text-sm text-green-700">{a.nome}</span>
                        <button onClick={() => removeAllegato(i)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="p-4 bg-[#e5ddd5] rounded-xl">
                <p className="text-xs text-gray-600 mb-2">Anteprima messaggio:</p>
                <div className="bg-[#dcf8c6] p-3 rounded-lg max-w-[80%] shadow-sm">
                  <p className="text-sm whitespace-pre-wrap">
                    {messaggio
                      .replace(/{nome}/g, "Centro Estetico Esempio")
                      .replace(/{citta}/g, "Milano")
                      .replace(/{regione}/g, "Lombardia")
                      || "Il tuo messaggio apparirà qui..."}
                  </p>
                  {allegati.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-green-300 text-xs text-gray-600">
                      📎 {allegati.length} allegato/i
                    </div>
                  )}
                  <p className="text-xs text-gray-500 text-right mt-1">
                    {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">
              <ChevronLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            <Button
              onClick={() => setStep(3)}
              disabled={!messaggio.trim()}
              className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 px-8"
            >
              Avanti <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Schedule & Settings */}
      {step === 3 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-green-600" />
                Impostazioni Invio Anti-Blocco
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Warning */}
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Protezione Anti-Blocco Meta</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Per evitare che WhatsApp blocchi il tuo numero, il sistema aprirà i link automaticamente 
                      con pause tra un messaggio e l'altro. Segui le impostazioni consigliate.
                    </p>
                  </div>
                </div>
              </div>

              {/* Interval Setting */}
              <div className="space-y-3">
                <Label className="flex items-center justify-between">
                  <span>Intervallo tra messaggi</span>
                  <span className="text-lg font-bold text-green-600">{intervalloSecondi} secondi</span>
                </Label>
                <Slider
                  value={[intervalloSecondi]}
                  onValueChange={(v) => setIntervalloSecondi(v[0])}
                  min={30}
                  max={120}
                  step={5}
                  className="py-4"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>30s (più veloce)</span>
                  <span className="text-green-600 font-medium">45s consigliato</span>
                  <span>120s (più sicuro)</span>
                </div>
              </div>

              {/* Messages per session */}
              <div className="space-y-3">
                <Label className="flex items-center justify-between">
                  <span>Messaggi prima della pausa lunga</span>
                  <span className="text-lg font-bold text-green-600">{messaggiPerSessione}</span>
                </Label>
                <Slider
                  value={[messaggiPerSessione]}
                  onValueChange={(v) => setMessaggiPerSessione(v[0])}
                  min={10}
                  max={50}
                  step={5}
                  className="py-4"
                />
                <p className="text-xs text-gray-500">
                  Dopo {messaggiPerSessione} messaggi, pausa di {pausaMinuti} minuti
                </p>
              </div>

              {/* Pause duration */}
              <div className="space-y-3">
                <Label className="flex items-center justify-between">
                  <span>Durata pausa lunga</span>
                  <span className="text-lg font-bold text-green-600">{pausaMinuti} minuti</span>
                </Label>
                <Slider
                  value={[pausaMinuti]}
                  onValueChange={(v) => setPausaMinuti(v[0])}
                  min={15}
                  max={60}
                  step={5}
                  className="py-4"
                />
              </div>

              {/* Time estimate */}
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">Tempo stimato totale</p>
                    <p className="text-sm text-green-600">{selectedContatti.length} messaggi</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-700">
                      {Math.ceil(
                        (selectedContatti.length * intervalloSecondi / 60) + 
                        (Math.floor(selectedContatti.length / messaggiPerSessione) * pausaMinuti)
                      )} min
                    </p>
                    <p className="text-xs text-green-600">circa</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Riepilogo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Destinatari</p>
                  <p className="text-xl font-bold">{selectedContatti.length}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Allegati</p>
                  <p className="text-xl font-bold">{allegati.length}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                  <p className="text-sm text-gray-500">Anteprima messaggio</p>
                  <p className="text-sm truncate">{messaggio.slice(0, 100)}...</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-full">
              <ChevronLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            <Button
              onClick={startSending}
              className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 px-8"
            >
              <Play className="w-4 h-4 mr-2" /> Avvia Invio
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Sending */}
      {step === 4 && (
        <div className="space-y-4">
          {/* Progress Card */}
          <Card className="border-2 border-green-300">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
                  {currentIndex >= whatsappLinks.length ? (
                    <Check className="w-12 h-12 text-white" />
                  ) : sendingPaused ? (
                    <Pause className="w-12 h-12 text-white" />
                  ) : (
                    <Phone className="w-12 h-12 text-white animate-pulse" />
                  )}
                </div>
                <h2 className="text-3xl font-bold text-gray-800">
                  {currentIndex >= whatsappLinks.length ? "🎉 Invio Completato!" : 
                   sendingPaused ? "⏸️ In Pausa" : "📤 Invio in Corso..."}
                </h2>
              </div>

              {/* Progress Bar with numbers */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-bold text-green-600 text-lg">{sentCount} inviati</span>
                  <span className="text-gray-500">{whatsappLinks.length - sentCount} rimanenti</span>
                  <span className="font-bold text-lg">{whatsappLinks.length} totali</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 h-6 rounded-full transition-all duration-500 flex items-center justify-center"
                    style={{ width: `${Math.max((sentCount / whatsappLinks.length) * 100, 5)}%` }}
                  >
                    <span className="text-white text-xs font-bold">
                      {Math.round((sentCount / whatsappLinks.length) * 100)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Timer visivo grande */}
              {countdown > 0 && currentIndex < whatsappLinks.length && (
                <div className="text-center mb-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-300">
                  <p className="text-lg text-amber-700 mb-2 font-medium">
                    {countdown > 60 ? "⏰ Pausa di sicurezza in corso..." : "⏱️ Prossimo messaggio tra:"}
                  </p>
                  <div className="relative w-32 h-32 mx-auto">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle cx="64" cy="64" r="56" stroke="#fde68a" strokeWidth="8" fill="none" />
                      <circle 
                        cx="64" cy="64" r="56" 
                        stroke="#f59e0b" 
                        strokeWidth="8" 
                        fill="none"
                        strokeDasharray={`${(countdown / intervalloSecondi) * 352} 352`}
                        className="transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-mono font-bold text-amber-600">
                        {formatTime(countdown)}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={skipToNext}
                    className="mt-4 rounded-full bg-amber-500 hover:bg-amber-600"
                  >
                    <SkipForward className="w-4 h-4 mr-2" /> Salta attesa e apri ora
                  </Button>
                </div>
              )}

              {/* Current Contact Card */}
              {currentIndex < whatsappLinks.length && (
                <div className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl mb-6 border-2 border-green-200">
                  <p className="text-xs text-green-600 mb-1 uppercase tracking-wide font-semibold">Prossimo destinatario #{currentIndex + 1}:</p>
                  <p className="font-bold text-2xl text-gray-800">{whatsappLinks[currentIndex]?.nome}</p>
                  <p className="text-lg text-gray-600">
                    📍 {whatsappLinks[currentIndex]?.citta} - {whatsappLinks[currentIndex]?.regione}
                  </p>
                  <p className="text-xl text-green-600 font-mono mt-2">📱 {whatsappLinks[currentIndex]?.cellulare}</p>
                </div>
              )}

              {/* PULSANTE PRINCIPALE - UN SOLO CLICK */}
              {currentIndex < whatsappLinks.length && (
                <Button
                  onClick={() => {
                    window.open(whatsappLinks[currentIndex].link, '_blank');
                    const updatedLinks = [...whatsappLinks];
                    updatedLinks[currentIndex].sent = true;
                    updatedLinks[currentIndex].sentAt = new Date().toLocaleTimeString();
                    setWhatsappLinks(updatedLinks);
                    setSentCount(prev => prev + 1);
                    setCurrentIndex(prev => prev + 1);
                    if (currentIndex + 1 < whatsappLinks.length) {
                      startCountdown();
                    }
                  }}
                  className="w-full h-16 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-xl font-bold shadow-lg mb-4"
                >
                  <Send className="w-6 h-6 mr-3" />
                  APRI WHATSAPP PER "{whatsappLinks[currentIndex]?.nome?.substring(0, 20)}..."
                </Button>
              )}

              {/* Pulsanti per apertura multipla 5-10-15-20-25 */}
              {currentIndex < whatsappLinks.length && (
                <div className="space-y-3">
                  <p className="text-center text-sm text-gray-600 font-medium">Oppure apri più link insieme:</p>
                  <div className="grid grid-cols-5 gap-2">
                    {[5, 10, 15, 20, 25].map(num => {
                      const remaining = whatsappLinks.length - currentIndex;
                      const canOpen = Math.min(num, remaining);
                      return (
                        <Button
                          key={num}
                          variant="outline"
                          disabled={remaining === 0}
                          onClick={() => {
                            const endIndex = Math.min(currentIndex + canOpen, whatsappLinks.length);
                            const updatedLinks = [...whatsappLinks];
                            
                            for (let i = currentIndex; i < endIndex; i++) {
                              setTimeout(() => {
                                window.open(whatsappLinks[i].link, '_blank');
                                updatedLinks[i].sent = true;
                                updatedLinks[i].sentAt = new Date().toLocaleTimeString();
                                setWhatsappLinks([...updatedLinks]);
                              }, (i - currentIndex) * 2500); // 2.5 secondi tra ogni apertura
                            }
                            
                            setSentCount(prev => prev + canOpen);
                            setCurrentIndex(endIndex);
                            
                            if (endIndex < whatsappLinks.length) {
                              setTimeout(() => startCountdown(), canOpen * 2500);
                            }
                            
                            toast.success(`Apertura di ${canOpen} link in corso...`);
                          }}
                          className={`rounded-xl h-14 flex flex-col items-center justify-center ${
                            remaining >= num ? 'border-green-300 hover:bg-green-50' : 'opacity-50'
                          }`}
                        >
                          <span className="text-lg font-bold">{num}</span>
                          <span className="text-[10px]">link</span>
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-center text-xs text-gray-500">
                    ⚠️ I link si apriranno con 2.5 secondi di intervallo tra uno e l'altro
                  </p>
                </div>
              )}

              {/* Controlli pausa/riprendi */}
              <div className="flex flex-wrap gap-2 justify-center mt-6">
                {currentIndex < whatsappLinks.length && (
                  <>
                    {sendingPaused ? (
                      <Button onClick={resumeSending} className="rounded-full bg-green-600 hover:bg-green-700">
                        <Play className="w-4 h-4 mr-2" /> Riprendi Timer
                      </Button>
                    ) : countdown > 0 && (
                      <Button onClick={pauseSending} variant="outline" className="rounded-full">
                        <Pause className="w-4 h-4 mr-2" /> Pausa Timer
                      </Button>
                    )}
                  </>
                )}
                
                <Button 
                  variant={currentIndex >= whatsappLinks.length ? "default" : "outline"}
                  onClick={() => {
                    clearInterval(countdownRef.current);
                    setStep(1);
                    setSendingActive(false);
                    setSelectedContatti([]);
                    setWhatsappLinks([]);
                    setSentCount(0);
                    setCurrentIndex(0);
                  }} 
                  className="rounded-full"
                >
                  {currentIndex >= whatsappLinks.length ? "🚀 Nuova Campagna" : "❌ Annulla e Ricomincia"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista con checkbox per tracciare gli inviati */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Tracciamento Invii
                </CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    Inviati: {sentCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    Corrente: 1
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    In attesa: {whatsappLinks.length - sentCount - (currentIndex < whatsappLinks.length ? 1 : 0)}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500 w-10">✓</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">Nome</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">Città</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">Telefono</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">Stato</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">Ora</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500 w-20">Azione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whatsappLinks.map((link, idx) => (
                      <tr
                        key={idx}
                        className={`border-b transition-colors ${
                          link.sent ? 'bg-green-50' : 
                          idx === currentIndex ? 'bg-amber-50 border-l-4 border-l-amber-500' : 
                          'hover:bg-gray-50'
                        }`}
                      >
                        <td className="p-2 text-sm font-mono">{idx + 1}</td>
                        <td className="p-2">
                          <Checkbox 
                            checked={link.sent} 
                            disabled
                            className={link.sent ? 'bg-green-500 border-green-500' : ''}
                          />
                        </td>
                        <td className="p-2 text-sm font-medium truncate max-w-[150px]">{link.nome}</td>
                        <td className="p-2 text-sm text-gray-600">{link.citta}</td>
                        <td className="p-2 text-sm font-mono text-green-600">{link.cellulare}</td>
                        <td className="p-2">
                          {link.sent ? (
                            <Badge className="bg-green-100 text-green-700">
                              <Check className="w-3 h-3 mr-1" /> Inviato
                            </Badge>
                          ) : idx === currentIndex ? (
                            <Badge className="bg-amber-100 text-amber-700 animate-pulse">
                              ⏳ Prossimo
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">
                              In attesa
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 text-xs text-gray-500">
                          {link.sentAt || '-'}
                        </td>
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              window.open(link.link, '_blank');
                              if (!link.sent) {
                                const updatedLinks = [...whatsappLinks];
                                updatedLinks[idx].sent = true;
                                updatedLinks[idx].sentAt = new Date().toLocaleTimeString();
                                setWhatsappLinks(updatedLinks);
                                if (idx === currentIndex) {
                                  setSentCount(prev => prev + 1);
                                  setCurrentIndex(prev => prev + 1);
                                }
                              }
                            }}
                            className="h-7 px-2 text-xs rounded-full"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Apri
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageLayout>
  );
};

export default WhatsAppAutomationPage;
